// LoginScreen — gates the app behind email + password (P-045 / D-140).
//
// Renders when AuthGate finds no valid session. Submits the form via
// the dynamically-imported `auth.login`; on success calls `onLogin`
// which re-checks the session and proceeds into the app. Wrong email
// + wrong password produce the same generic error string so the screen
// doesn't leak which addresses are registered (no email enumeration —
// PRD §Functional requirements).
//
// Layout mirrors the existing app shell: navy header with the chapter
// wordmark + "Email Builder" label, centered card on a white body
// with a YPO-navy submit button. Form-field styling is a one-off here
// — the chapter Block FormGenerator's `<TextField>` is built for the
// schema-driven block-editor surface, not standalone screens.

import { useState } from 'preact/hooks'

export interface LoginScreenProps {
  readonly onLogin: () => void
}

export function LoginScreen({ onLogin }: LoginScreenProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault()
    if (submitting) return
    setError(null)
    setSubmitting(true)
    try {
      const { login } = await import('../../lib/auth')
      await login(email, password)
      onLogin()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid email or password.')
      setSubmitting(false)
    }
  }

  return (
    <div class="flex min-h-screen flex-col bg-white font-sans text-gray-900">
      <header class="bg-ypo-navy text-white">
        <div class="mx-auto flex max-w-screen-lg items-center gap-3 px-4 py-3">
          <img
            src="/ypo-s7-white.png"
            alt="YPO Southern 7"
            width={250}
            height={42}
            class="block"
          />
          <span aria-hidden="true" class="select-none text-2xl font-light text-gray-300">
            |
          </span>
          <span class="text-base font-medium text-white">Email Builder</span>
        </div>
      </header>
      <main class="flex flex-1 items-center justify-center px-4 py-12">
        <form
          onSubmit={handleSubmit}
          aria-labelledby="login-heading"
          class="w-full max-w-sm rounded border border-gray-200 bg-white p-6 shadow-sm"
        >
          <h1
            id="login-heading"
            class="m-0 mb-1 text-xl font-semibold text-gray-900"
          >
            Sign in
          </h1>
          <p class="m-0 mb-4 text-sm text-gray-600">
            Chapter staff access — enter your email and password.
          </p>

          <label class="mb-3 block">
            <span class="mb-1 block text-sm font-medium text-gray-800">Email</span>
            <input
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onInput={(e) => setEmail((e.currentTarget as HTMLInputElement).value)}
              disabled={submitting}
              class="block w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-ypo-navy focus:outline-none focus:ring-1 focus:ring-ypo-navy disabled:bg-gray-50"
            />
          </label>

          <label class="mb-4 block">
            <span class="mb-1 block text-sm font-medium text-gray-800">Password</span>
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onInput={(e) => setPassword((e.currentTarget as HTMLInputElement).value)}
              disabled={submitting}
              class="block w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-ypo-navy focus:outline-none focus:ring-1 focus:ring-ypo-navy disabled:bg-gray-50"
            />
          </label>

          {error !== null && (
            <p
              role="alert"
              class="m-0 mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            class="w-full rounded bg-ypo-navy px-3 py-2 text-sm font-medium text-white hover:bg-ypo-navy-deep disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </main>
    </div>
  )
}
